import "katex/dist/katex.min.css";
import { Loader2Icon, PlusSquare } from "lucide-react";
import { type ComponentProps, createContext, isValidElement, type ReactNode, useContext, useMemo } from "react";
import Markdown, { type Components, type ExtraProps } from "react-markdown";
import rehypeKatex from "rehype-katex";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";

import { useAiChatOptional } from "~/ai-chat/feature/use-ai-chat";
import { parseEntityLink } from "~/ai-chat/model/entity-link";
import type { FilePreviewRequest } from "~/ai-chat/model/file-preview-types";
import type { MeshViewRequest } from "~/ai-chat/model/mesh-file-types";
import {
  parseSaveResourceAction,
  SAVE_RESOURCE_LANGUAGE,
  type SaveResourceAction,
} from "~/ai-chat/model/save-resource-action";
import { ChatEntityChip, type ChatEntityChipProps } from "~/ai-chat/ui/chat-entity-chip";
import { cn } from "~/utils/cn";

export type RenderSaveResourceAction = (action: SaveResourceAction, blockKey: number) => ReactNode;
// Lets the feature layer wrap an entity chip (e.g. to verify its target) without ui/ fetching.
export type RenderEntityChip = (props: ChatEntityChipProps) => ReactNode;

interface ChatMarkdownProps {
  content: string;
  isStreaming?: boolean;
  renderSaveResourceAction?: RenderSaveResourceAction;
  renderEntityChip?: RenderEntityChip;
  onViewMesh?: (mesh: MeshViewRequest) => void;
  onPreview?: (file: FilePreviewRequest) => void;
}

// Per-render inputs the element renderers read. They travel through context so
// COMPONENTS can be a module constant: react-markdown mounts each entry as a
// component type, so a map rebuilt per render would remount every element on
// every render and wipe any state held beneath it (a save card's, for one).
interface RenderOptions {
  isStreaming: boolean;
  renderSaveResourceAction?: RenderSaveResourceAction;
  renderEntityChip?: RenderEntityChip;
  onViewMesh?: (mesh: MeshViewRequest) => void;
  onPreview?: (file: FilePreviewRequest) => void;
  onInsertCode?: (code: string) => void;
}

const RenderOptionsContext = createContext<RenderOptions>({ isStreaming: false });

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

const HEADING_CLASSES = "mt-2 mb-0.5 text-sm leading-snug font-semibold first:mt-0 last:mb-0";

export function ChatMarkdown({
  content,
  isStreaming = false,
  renderSaveResourceAction,
  renderEntityChip,
  onViewMesh,
  onPreview,
}: ChatMarkdownProps) {
  const chat = useAiChatOptional();
  const onInsertCode = chat?.onInsertCode ?? undefined;
  const options = useMemo<RenderOptions>(
    () => ({ isStreaming, renderSaveResourceAction, renderEntityChip, onViewMesh, onPreview, onInsertCode }),
    [isStreaming, renderSaveResourceAction, renderEntityChip, onViewMesh, onPreview, onInsertCode],
  );

  return (
    <RenderOptionsContext.Provider value={options}>
      <Markdown remarkPlugins={REMARK_PLUGINS} rehypePlugins={REHYPE_PLUGINS} components={COMPONENTS}>
        {content}
      </Markdown>
    </RenderOptionsContext.Provider>
  );
}

function MarkdownAnchor({ children, href, ...props }: ComponentProps<"a"> & ExtraProps) {
  const { onViewMesh, onPreview, renderEntityChip } = useContext(RenderOptionsContext);
  const entityLink = href ? parseEntityLink(href) : null;
  if (entityLink) {
    const chipProps: ChatEntityChipProps = { link: entityLink, children, onViewMesh, onPreview };
    return renderEntityChip ? renderEntityChip(chipProps) : <ChatEntityChip {...chipProps} />;
  }
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary underline underline-offset-2"
      {...props}
    >
      {children}
    </a>
  );
}

function MarkdownPre({ node, children, ...props }: ComponentProps<"pre"> & ExtraProps) {
  const { isStreaming, renderSaveResourceAction, onInsertCode } = useContext(RenderOptionsContext);

  const saveResourceCode = saveResourceCodeOf(children);
  if (saveResourceCode !== null) {
    // Mid-stream the fence is incomplete (possibly unclosed, partial
    // JSON) — show a placeholder instead of flashing raw JSON, and only
    // parse once the message has finished streaming.
    if (isStreaming) return <SaveResourcePendingBlock />;
    const action = parseSaveResourceAction(saveResourceCode);
    if (action !== null && renderSaveResourceAction) {
      return renderSaveResourceAction(action, node?.position?.start.offset ?? 0);
    }
  }

  const codeChild = children as React.ReactElement<{ className?: string; children?: string }>;
  const lang = codeChild?.props?.className ?? "";
  const isPython = /language-python/.test(lang);
  const codeText = typeof codeChild?.props?.children === "string" ? codeChild.props.children : null;
  const showInsert = Boolean(isPython && codeText && onInsertCode);

  return (
    <div className="relative my-1 first:mt-0 last:mb-0">
      <pre
        className={cn(
          "bg-background/60 max-w-full overflow-x-auto rounded-md p-1.5 text-xs whitespace-pre",
          // Room for the overlaid Insert button, so a line's tail can scroll clear of it.
          showInsert && "pr-16",
        )}
        {...props}
      >
        {children}
      </pre>
      {isPython && codeText && onInsertCode && (
        <button
          type="button"
          onClick={() => onInsertCode(codeText)}
          className={cn(
            "absolute top-1 right-1 flex items-center gap-1 rounded px-1.5 py-0.5",
            "bg-background/80 text-muted-foreground hover:text-foreground",
            "text-[0.7rem] leading-none transition-colors",
          )}
          aria-label="Insert code into active cell"
        >
          <PlusSquare className="size-3" />
          Insert
        </button>
      )}
    </div>
  );
}

const COMPONENTS: Components = {
  p({ children, ...props }) {
    return (
      <p className="my-1 first:mt-0 last:mb-0" {...props}>
        {children}
      </p>
    );
  },
  ul({ children, ...props }) {
    return (
      <ul className="my-1 list-disc pl-4 first:mt-0 last:mb-0" {...props}>
        {children}
      </ul>
    );
  },
  ol({ children, ...props }) {
    return (
      <ol className="my-1 list-decimal pl-4 first:mt-0 last:mb-0" {...props}>
        {children}
      </ol>
    );
  },
  li({ children, ...props }) {
    return (
      <li className="my-0 leading-snug" {...props}>
        {children}
      </li>
    );
  },
  h1({ children, ...props }) {
    return (
      <h1 className={HEADING_CLASSES} {...props}>
        {children}
      </h1>
    );
  },
  h2({ children, ...props }) {
    return (
      <h2 className={HEADING_CLASSES} {...props}>
        {children}
      </h2>
    );
  },
  h3({ children, ...props }) {
    return (
      <h3 className={HEADING_CLASSES} {...props}>
        {children}
      </h3>
    );
  },
  h4({ children, ...props }) {
    return (
      <h4 className={HEADING_CLASSES} {...props}>
        {children}
      </h4>
    );
  },
  h5({ children, ...props }) {
    return (
      <h5 className={HEADING_CLASSES} {...props}>
        {children}
      </h5>
    );
  },
  h6({ children, ...props }) {
    return (
      <h6 className={HEADING_CLASSES} {...props}>
        {children}
      </h6>
    );
  },
  blockquote({ children, ...props }) {
    return (
      <blockquote
        className="border-muted-foreground/40 text-muted-foreground my-1 border-l-2 pl-2 italic first:mt-0 last:mb-0"
        {...props}
      >
        {children}
      </blockquote>
    );
  },
  hr(props) {
    return <hr className="border-border my-1.5" {...props} />;
  },
  strong({ children, ...props }) {
    return (
      <strong className="font-semibold" {...props}>
        {children}
      </strong>
    );
  },
  em({ children, ...props }) {
    return (
      <em className="italic" {...props}>
        {children}
      </em>
    );
  },
  a: MarkdownAnchor,
  code({ className, children, ...props }) {
    const isBlock = /\blanguage-/.test(className ?? "") || String(children).includes("\n");
    if (isBlock) {
      const codeString = String(children).replace(/\n$/, "");
      return (
        <code className={cn("font-mono text-xs", className)} {...props}>
          {codeString}
        </code>
      );
    }
    return (
      <code
        className={cn("bg-background/60 rounded px-1 py-0.5 font-mono text-[0.8125rem] break-all", className)}
        {...props}
      >
        {children}
      </code>
    );
  },
  pre: MarkdownPre,
  table({ children, ...props }) {
    return (
      <div className="my-1 overflow-x-auto first:mt-0 last:mb-0">
        <table className="border-border w-full border-collapse border text-xs" {...props}>
          {children}
        </table>
      </div>
    );
  },
  th({ children, ...props }) {
    return (
      <th className="bg-background/40 border-border border px-1.5 py-0.5 text-left font-semibold" {...props}>
        {children}
      </th>
    );
  },
  td({ children, ...props }) {
    return (
      <td className="border-border border px-1.5 py-0.5" {...props}>
        {children}
      </td>
    );
  },
};

function saveResourceCodeOf(children: ReactNode): string | null {
  if (!isValidElement(children)) return null;
  const codeProps = children.props as { className?: string; children?: ReactNode };
  if (!codeProps.className?.includes(`language-${SAVE_RESOURCE_LANGUAGE}`)) return null;
  const code = codeProps.children;
  if (typeof code === "string") return code;
  if (Array.isArray(code) && code.every((part) => typeof part === "string")) return code.join("");
  return null;
}

function SaveResourcePendingBlock() {
  return (
    <div className="border-border bg-card my-1 flex items-center gap-2 rounded-lg border p-2.5 first:mt-0 last:mb-0">
      <Loader2Icon className="text-muted-foreground size-3.5 motion-safe:animate-spin" aria-hidden="true" />
      <span className="text-muted-foreground text-xs">Preparing resource…</span>
    </div>
  );
}
